// tslint:disable: max-file-line-count
import {
  EBuildingStatus,
  ECategoryType,
  EObjectType,
  EOfficeType,
  ERentTime,
  ERoomType,
  IJsonQuery,
} from '@cian/api-models/common/json_query';

export const enum FDealType {
  Unexpected   = 0, // Do not use
  Sale         = 1 << 0,
  RentLongterm = 1 << 1,
  RentDaily    = 1 << 2,
  Rent         = RentLongterm | RentDaily,
  Any          = Sale | Rent,
}

export const enum FOfferType {
  Unexpected            = 0, // Do not use
  FlatOld               = 1 << 1,
  FlatNew               = 1 << 2,
  Flat                  = FlatOld | FlatNew,
  FlatShared            = 1 << 3,
  Room                  = 1 << 4,
  Bed                   = 1 << 5,
  Urban                 = Flat | FlatShared | Room | Bed,
  House                 = 1 << 6,
  HousePart             = 1 << 7,
  Townhouse             = 1 << 8,
  Land                  = 1 << 9,
  Suburban              = House | HousePart | Townhouse | Land,
  Residential           = Urban | Suburban,
  // Офис
  Office                = 1 << 10,
  // Торговая площадь
  TradeArea             = 1 << 11,
  // Склад
  Warehouse             = 1 << 12,
  // ПСН
  FreeAppointmentObject = 1 << 13,
  // Общепит
  PublicCatering        = 1 << 14,
  // Гараж
  Garage                = 1 << 15,
  // Производство
  Manufacture           = 1 << 16,
  // Автосервис
  AutoService           = 1 << 17,
  // Готовый бизнес
  Business              = 1 << 18,
  // Здание
  Building              = 1 << 19,
  // Бытовые услуги
  DomesticServices      = 1 << 20,
  // Коммерческая земля
  CommercialLand        = 1 << 21,
  Commercial            = Office | TradeArea | Warehouse |
                          FreeAppointmentObject | PublicCatering | Garage |
                          Manufacture | AutoService | Business |
                          Building | DomesticServices | CommercialLand,
  Any                   = Residential | Commercial,
}

export const dealTypeFromJsonQuery = (jsonQuery: IJsonQuery): FDealType => {
  const type = jsonQuery._type;
  const rentTime = jsonQuery.for_day && jsonQuery.for_day.value;

  if (type.endsWith('sale')) {
    return FDealType.Sale;
  }

  if (type.endsWith('rent')) {
    if (rentTime === ERentTime.NotDaily) {
      return FDealType.RentLongterm;
    } else if (rentTime === ERentTime.Daily) {
      return FDealType.RentDaily;
    }

    return FDealType.Rent;
  }

  return FDealType.Unexpected;
};

export const offerTypeFromJsonQuery = (jsonQuery: IJsonQuery): FOfferType => {
  const type = jsonQuery._type;
  const dealType = dealTypeFromJsonQuery(jsonQuery);
  const room = jsonQuery.room && jsonQuery.room.value;
  const buildingStatus = jsonQuery.building_status && jsonQuery.building_status.value;
  const fromDeveloper = jsonQuery.from_developer && jsonQuery.from_developer.value;
  const withNewbuildings = jsonQuery.with_newobject && jsonQuery.with_newobject.value;

  if (type.startsWith('flat')) {
    if (room && room.some((r) => [ERoomType.FlatShared, ERoomType.Room, ERoomType.Bed].includes(r))) {
      let offerType = FOfferType.Unexpected;

      if (room.includes(ERoomType.FlatShared)) {
        offerType |= FOfferType.FlatShared;
      }
      if (room.includes(ERoomType.Room)) {
        offerType |= FOfferType.Room;
      }
      if (room.includes(ERoomType.Bed)) {
        offerType |= FOfferType.Bed;
      }

      return offerType;
    }

    if (buildingStatus && buildingStatus === EBuildingStatus.New) {
      return FOfferType.FlatNew;
    }

    if (fromDeveloper !== undefined) {
      return FOfferType.FlatNew;
    }

    if (withNewbuildings) {
      return FOfferType.FlatNew;
    }

    if (buildingStatus && buildingStatus === EBuildingStatus.Old) {
      return FOfferType.FlatOld;
    }

    if (withNewbuildings !== undefined) {
      return FOfferType.FlatOld;
    }

    if (dealType & FDealType.Rent) {
      return FOfferType.FlatOld;
    }

    if (type === 'flatsale') {
      return FOfferType.Flat;
    }

    return FOfferType.Urban;
  }

  if (type.startsWith('suburban')) {
    const objectType = jsonQuery.object_type && jsonQuery.object_type.value;

    if (objectType) {
      if (objectType.length > 1) {
        let offerType = FOfferType.Unexpected;
        if (objectType.includes(EObjectType.House)) {
          offerType |= FOfferType.House;
        }
        if (objectType.includes(EObjectType.Housepart)) {
          offerType |= FOfferType.HousePart;
        }
        if (objectType.includes(EObjectType.Townhouse)) {
          offerType |= FOfferType.Townhouse;
        }
        if (objectType.includes(EObjectType.Land)) {
          offerType |= FOfferType.Land;
        }

        return offerType;
      } else if (objectType.includes(EObjectType.House)) {
        return FOfferType.House;
      } else if (objectType.includes(EObjectType.Housepart)) {
        return FOfferType.HousePart;
      } else if (objectType.includes(EObjectType.Townhouse)) {
        return FOfferType.Townhouse;
      }
    }

    if (objectType && objectType.includes(EObjectType.Land)) {
      if ((dealType & FDealType.Rent) === 0) {
        return FOfferType.Land;
      } else {
        return FOfferType.CommercialLand;
      }
    }

    return FOfferType.Suburban;
  }

  if (type.startsWith('commercial')) {
    const officeType = jsonQuery.office_type && jsonQuery.office_type.value;
    const category = jsonQuery.category && jsonQuery.category.value[0];

    if (officeType) {
      let offerType = FOfferType.Unexpected;
      if (officeType.includes(EOfficeType.Office)) {
        offerType |= FOfferType.Office;
      }
      if (officeType.includes(EOfficeType.TradeArea)) {
        offerType |= FOfferType.TradeArea;
      }
      if (officeType.includes(EOfficeType.Warehouse)) {
        offerType |= FOfferType.Warehouse;
      }
      if (officeType.includes(EOfficeType.FreeAppointmentObject)) {
        offerType |= FOfferType.FreeAppointmentObject;
      }
      if (officeType.includes(EOfficeType.PublicCatering)) {
        offerType |= FOfferType.PublicCatering;
      }
      if (officeType.includes(EOfficeType.Garage)) {
        offerType |= FOfferType.Garage;
      }
      if (officeType.includes(EOfficeType.Manufacture)) {
        offerType |= FOfferType.Manufacture;
      }
      if (officeType.includes(EOfficeType.AutoService)) {
        offerType |= FOfferType.AutoService;
      }
      if (officeType.includes(EOfficeType.Business)) {
        offerType |= FOfferType.Business;
      }
      if (officeType.includes(EOfficeType.Building)) {
        offerType |= FOfferType.Building;
      }
      if (officeType.includes(EOfficeType.DomesticServices)) {
        offerType |= FOfferType.DomesticServices;
      }

      return offerType;
    }

    if (category && [ECategoryType.CommercialLandRent, ECategoryType.CommercialLandSale].includes(category)) {
      return FOfferType.CommercialLand;
    }

    return FOfferType.Commercial;
  }

  return FOfferType.Unexpected;
};

export const isParamAvailable = (
    currentDealType: FDealType,
    currentOfferType: FOfferType,
  ) => {
    return (allowedDealTypes: FDealType, allowedOfferTypes: FOfferType, custom?: boolean) => {
      if ((currentDealType & allowedDealTypes) === 0) {
        return false;
      }
      if ((currentOfferType & allowedOfferTypes) === 0) {
        return false;
      }
      if (custom !== undefined && !custom) {
        return false;
      }

      return true;
    };
};

export function checkParamType(paramType: FOfferType | FDealType) {
  return (allowedParamType: FOfferType | FDealType) =>
    (paramType & allowedParamType) !== 0;
}

export function isOfferTypeIncludes(currentOfferType: FOfferType, permissibleValue: FOfferType) {
  return (currentOfferType & permissibleValue) !== 0;
}

/**
 * Check is urban category
 * @param {FOfferType} offerType
 */
export function isUrban(offerType: FOfferType) {
  return (offerType & FOfferType.Urban) !== 0;
}

/**
 * Check is suburban category
 * @param {FOfferType} offerType
 */
export function isSuburban(offerType: FOfferType) {
  return (offerType & FOfferType.Suburban) !== 0;
}

/**
 * Check is commercial category
 * @param {FOfferType} offerType
 */
export function isCommercial(offerType: FOfferType) {
  return (offerType & FOfferType.Commercial) !== 0;
}

/**
 * Check is residential category
 * @param {FOfferType} offerType
 */
export function isResidential(offerType: FOfferType) {
  return (offerType & FOfferType.Residential) !== 0;
}

/**
 * Check is flat category
 * @param {FOfferType} offerType
 */
export function isFlat(offerType: FOfferType) {
  return (offerType & FOfferType.Flat) !== 0;
}
